/// <reference types="@types/google.maps" />

import { motion } from 'framer-motion'
import React, { Suspense, useEffect, useRef, useState } from 'react'
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService'

import { useMapApiKey } from 'src/shared/api/google-maps/use-map-api-key'
import { cn } from 'src/shared/utils/cn'

interface LocationAutoCompleteProps {
  onPlaceSelected: (place: PlaceResult) => void
  placeholder?: string
  types: string[]
  componentRestrictions?: google.maps.places.ComponentRestrictions
  refValue: 'street' | 'city' | 'full' | ''
  value?: string
  inputClassName?: string
  placeholderClassName?: string
}

export interface PlaceResult {
  street: string
  city: string
  state: string
  zip: string
  latitude: number
  longitude: number
}

export const LocationAutoCompleteContent: React.FC<LocationAutoCompleteProps> = ({
  onPlaceSelected,
  placeholder = 'Enter a location',
  types = ['address'],
  componentRestrictions = { country: 'us' },
  refValue,
  value,
  inputClassName,
  placeholderClassName,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState(value || '')
  const [showPredictions, setShowPredictions] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [inputFocused, setInputFocused] = useState(false)
  const { data: mapApiKey } = useMapApiKey()

  const shrinkPlaceholder = inputFocused || inputValue.length !== 0

  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: mapApiKey,
    debounce: 500,
  })

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = value || ''
      setInputValue(value || '')
    }
  }, [value])

  useEffect(() => {
    // Reset selected index when predictions change
    setSelectedIndex(-1)
  }, [placePredictions])

  const handlePlaceDetails = (placeId: string) => {
    if (!placesService) return

    placesService.getDetails(
      {
        placeId: placeId,
      },
      (placeDetails: google.maps.places.PlaceResult | null) => {
        if (!placeDetails) return

        const addressComponents = placeDetails.address_components || []
        const geometry = placeDetails.geometry || {}

        const getComponent = (types: string[]) => {
          for (const type of types) {
            const component = addressComponents.find((component) => component.types.includes(type))
            if (component) {
              if (type === 'administrative_area_level_1' || type === 'administrative_area_level_2') {
                return component.short_name
              }
              return component.long_name
            }
          }
          return ''
        }

        const streetNumber = getComponent(['street_number'])
        const route = getComponent(['route'])
        const street = `${streetNumber} ${route}`.trim()
        const city = getComponent([
          'locality',
          'postal_town',
          'sublocality',
          'neighborhood',
          'administrative_area_level_2',
        ])
        const state = getComponent(['administrative_area_level_1', 'administrative_area_level_2'])
        const zip = getComponent(['postal_code'])

        const placeResult: PlaceResult = {
          street,
          city,
          state,
          zip,
          latitude: geometry.location?.lat() || 0,
          longitude: geometry.location?.lng() || 0,
        }

        onPlaceSelected(placeResult)

        if (inputRef.current) {
          let displayValue = ''
          switch (refValue) {
            case 'street':
              displayValue = street
              break
            case 'city':
              displayValue = `${city}, ${state}`
              break
            case 'full':
              displayValue = `${street}, ${city}, ${state} ${zip}`
              break
          }
          inputRef.current.value = displayValue
          setInputValue(displayValue)
        }
        setShowPredictions(false)
        setSelectedIndex(-1)
      },
    )
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setShowPredictions(true)
    if (value.length > 1) {
      getPlacePredictions({
        input: value,
        types,
        componentRestrictions,
      })
    } else {
      setShowPredictions(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showPredictions || !placePredictions.length) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < placePredictions.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0) {
          handlePlaceDetails(placePredictions[selectedIndex].place_id)
        }
        break
      case 'Escape':
        setShowPredictions(false)
        setSelectedIndex(-1)
        break
    }
  }

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowPredictions(false)
        setSelectedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className="relative flex min-h-[56px] w-full cursor-text flex-col justify-start rounded-lg bg-accent">
      <motion.label
        initial={false}
        animate={{
          top: shrinkPlaceholder ? 8 : 18,
        }}
        style={{
          fontSize: shrinkPlaceholder ? '11px' : '16px',
        }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className={cn('pointer-events-none absolute left-3 z-10 font-medium text-muted', placeholderClassName)}
      >
        {placeholder}
      </motion.label>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (inputValue.length > 2) {
            setShowPredictions(true)
          }
          setInputFocused(true)
        }}
        onBlur={() => setInputFocused(false)}
        className={cn(
          'w-full flex-1 rounded-lg bg-transparent px-3 pb-3 pt-[22px] text-base text-foreground outline-none transition-all placeholder:text-muted',
          !shrinkPlaceholder && 'pb-[16px] pt-[16px]',
          shrinkPlaceholder && 'pb-[6px] pt-[22px]',
          inputClassName,
        )}
        autoComplete="off"
        data-lpignore="true"
        data-form-type="other"
        role="combobox"
        aria-expanded={showPredictions}
        aria-controls="location-predictions"
        aria-autocomplete="list"
      />
      {showPredictions && placePredictions.length > 0 && (
        <div
          ref={dropdownRef}
          id="location-predictions"
          role="listbox"
          className="absolute top-full z-50 mt-1 w-full rounded-lg bg-accent shadow-sm"
        >
          <ul className="py-1">
            {placePredictions.map((prediction, index) => (
              <li
                key={prediction.place_id}
                role="option"
                aria-selected={index === selectedIndex}
                className={`cursor-pointer px-4 py-2 ${
                  index === selectedIndex
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground'
                }`}
                onClick={() => handlePlaceDetails(prediction.place_id)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {prediction.description}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const LocationAutocompleteFormFieldSkeleton = () => {
  return (
    <div className="relative flex min-h-[56px] w-full cursor-text flex-col justify-start rounded-lg bg-accent">
      <div className="pointer-events-none absolute left-3 top-[18px] z-10 font-normal text-muted">Enter a location</div>
      <div className="w-full flex-1 rounded-lg bg-transparent px-3 py-[16px] text-base text-foreground outline-none transition-all" />
    </div>
  )
}

export const LocationAutoComplete: React.FC<LocationAutoCompleteProps> = ({
  onPlaceSelected,
  placeholder = 'Enter a location',
  types = ['address'],
  componentRestrictions = { country: 'us' },
  refValue,
  value,
  inputClassName,
  placeholderClassName,
}) => {
  return (
    <Suspense fallback={<LocationAutocompleteFormFieldSkeleton />}>
      <LocationAutoCompleteContent
        onPlaceSelected={onPlaceSelected}
        placeholder={placeholder}
        types={types}
        componentRestrictions={componentRestrictions}
        refValue={refValue}
        value={value}
        inputClassName={inputClassName}
        placeholderClassName={placeholderClassName}
      />
    </Suspense>
  )
}
